/**
 * Default Configuration for Settings Context
 *
 * Following the pattern from ScheduledMeetingsContext.
 */

export interface SettingsContextConfig {
  /**
   * Automatically fetch settings on mount
   */
  autoFetch: boolean;

  /**
   * Stale time in milliseconds (how long data is considered fresh)
   */
  staleTime: number;

  /**
   * Cache time in milliseconds (how long to keep data in cache)
   */
  cacheTime: number;

  /**
   * Enable background refetching
   */
  refetchOnWindowFocus: boolean;

  /**
   * Enable refetch on reconnect
   */
  refetchOnReconnect: boolean;
}

/**
 * Default configuration
 */
export const DEFAULT_CONFIG: SettingsContextConfig = {
  autoFetch: true,
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 30 * 60 * 1000, // 30 minutes (gcTime)
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

/**
 * TOS-specific configuration (longer cache time)
 *
 * Acceptance can be written outside this query's reach: the TeamHub settings
 * plugins run in their own React tree with no shared query cache, so nothing
 * invalidates this entry when a user accepts there. The hour-long staleTime
 * below would then keep serving "not accepted" to whatever gates on it
 * (SM-1168). The two refetch triggers narrow that window from opposite sides:
 * `refetchOnMount` covers the host swapping the Meet app out and back, and
 * `refetchOnWindowFocus` covers acceptance recorded in another tab or window.
 *
 * Neither covers acceptance in a second React root of the *same* page with no
 * remount in between. `SettingsModal` already calls `refetchTOS()` when it
 * opens, so that surface is safe; `MeetingOverlays` reads `needsAcceptance()`
 * straight from this cache and is the one still exposed.
 */
export const TOS_CONFIG = {
  autoFetch: true,
  staleTime: 60 * 60 * 1000, // 1 hour
  cacheTime: 24 * 60 * 60 * 1000, // 24 hours
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  refetchOnMount: 'always' as const,
};
